import type {CoreEntityType} from 'nutshell-core/types';

import type {
    CreateRulesFragment_contact,
    CreateRulesFragment_account,
    CreateRulesFragment_lead,
    CreateRulesFragment,
    CreateRulesFragment_lead_stagesetFieldRules as FieldRules,
} from 'nutshell-graphql-types';

import type {CreateRuleKey, StagesetRule} from './types';

// Takes a create rule object from the server and turns it into something
// our form expects. This is essentially mapping over arrays of models
// and just getting ids, and then translating _that_ back into a keyed object
export function translateCreateRules(
    createRules?:
        | CreateRulesFragment_contact
        | CreateRulesFragment_account
        | CreateRulesFragment_lead
        | null
) {
    if (!createRules) {
        return null;
    }

    return Object.keys(createRules).reduce<Record<string, any>>((prevValue, currentKey) => {
        if (currentKey === '__typename') {
            return prevValue;
        }

        const formKey = translateKeyToIds(currentKey);
        if (formKey) {
            return {
                ...prevValue,
                [formKey]: getValueFromKey(createRules, currentKey),
            };
        }

        return prevValue;
    }, {});
}

// Initially, the create rules are null. However, when create rules get updated and
// cleared out, our Nut_JsonObject still persists. This is a simple way to check
// to see if any properties are actually configured.
export function isCreateRuleEmpty(
    createRules?:
        | CreateRulesFragment_contact
        | CreateRulesFragment_account
        | CreateRulesFragment_lead
        | null
): boolean {
    if (!createRules) {
        return true;
    }

    let isEmpty = true;

    Object.keys(createRules)
        .filter((key) => key !== '__typename')
        .forEach((key) => {
            // @ts-expect-error
            if (isKeySingularIdField(key) && createRules[key]) {
                isEmpty = false;
            }

            // @ts-expect-error
            if (createRules[key] && createRules[key].length) {
                isEmpty = false;
            }

            if (createRules.__typename === 'FormCreateRulesLead' && key === 'stageset') {
                isEmpty = false;
            }
        });

    return isEmpty;
}

export function getTopLevelKey(entityType: CoreEntityType): CreateRuleKey {
    switch (entityType) {
        case 'accounts':
            return 'account';
        case 'contacts':
            return 'contact';
        case 'leads':
            return 'lead';
        default:
            return 'contact';
    }
}

export function translateKeyToIds(key: string): string | null | undefined {
    switch (key) {
        case 'tags':
            return 'tagIds';
        case 'audiences':
            return 'audienceIds';
        case 'stageset':
            return 'stagesetId';
        case 'shouldDedupe':
            return 'shouldDedupe';
        case 'stagesetFieldRules':
            return 'stagesetFieldRules';
        default:
            return null;
    }
}

export function isKeySingularIdField(key: string): boolean {
    switch (key) {
        case 'tags':
        case 'shouldDedupe':
        case 'stagesetFieldRules':
        case 'audiences': {
            return false;
        }
        case 'stageset':
            return true;
        default:
            return true;
    }
}

export function isKeyBooleanField(key: string): boolean {
    return key === 'shouldDedupe';
}

export function getCreateRules(
    createRules: CreateRulesFragment,
    entityType?: CoreEntityType | null
) {
    if (!entityType) {
        return null;
    }

    return translateCreateRules(createRules[getTopLevelKey(entityType)]);
}

export function canDeleteCreateRule(
    formCreateRules: CreateRulesFragment,
    entityType?: CoreEntityType | null
): boolean {
    if (!entityType) return false;

    const createRuleKey = getTopLevelKey(entityType);
    const createRules = formCreateRules[createRuleKey];

    return Boolean(createRules);
}

export function getValueFromKey(
    createRules:
        | CreateRulesFragment_account
        | CreateRulesFragment_contact
        | CreateRulesFragment_lead,
    key: string
) {
    if (isKeySingularIdField(key)) {
        // @ts-expect-error
        return createRules[key] ? createRules[key].id : null;
    }

    if (isKeyBooleanField(key)) {
        // @ts-expect-error
        return createRules[key];
    }

    if (key === 'stagesetFieldRules') {
        return createRules.__typename === 'FormCreateRulesLead'
            ? sanitizeStagesetRules(createRules[key])
            : null;
    }

    // @ts-expect-error
    return createRules[key].map((model) => {
        return model.id;
    });
}

function sanitizeStagesetRules(stagesetRules: FieldRules[]): StagesetRule[] {
    // @ts-expect-error
    let sanitizedStagesetRules = [];

    for (const {field, answerChoice, stageset} of stagesetRules) {
        // @ts-expect-error
        sanitizedStagesetRules = sanitizedStagesetRules.concat([
            {
                fieldId: field.id,
                answerChoiceId: answerChoice.id,
                stagesetId: stageset.id,
            },
        ]);
    }

    return sanitizedStagesetRules;
}
